import { useState } from 'react';
import API from '../api/axios';
import { Icon } from './SvgIcons';

/**
 * Shared bulk-upload-users modal (same UI for super admin, university admin,
 * college admin and HOD). Uses /tenants/:slug/bulk-upload — the server scopes
 * roles & college automatically based on the caller's role.
 *
 * Props: tenantSlug, onClose, onSuccess (called on the Done button).
 */
export default function BulkUploadModal({ tenantSlug, onClose, onSuccess }) {
  const [step, setStep] = useState('idle'); // idle | validating | preview | importing | done
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [previewPage, setPreviewPage] = useState(1);
  const [previewFilter, setPreviewFilter] = useState('all');
  const PPAGE = 25;

  const handleDownloadTemplate = async () => {
    try {
      const response = await API.get(`/tenants/${tenantSlug}/bulk-upload/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk_upload_template_${tenantSlug}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download template');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && /\.(xlsx|xls)$/i.test(f.name)) { setFile(f); setError(''); }
    else setError('Only .xlsx or .xls files are accepted');
  };

  const handleFileInput = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); setError(''); }
  };

  const handleValidate = async () => {
    if (!file) return;
    setStep('validating'); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await API.post(`/tenants/${tenantSlug}/bulk-upload?dry_run=true`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(data);
      setPreviewPage(1);
      setPreviewFilter('all');
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.message || 'Validation failed');
      setStep('idle');
    }
  };

  const handleImport = async () => {
    setStep('importing'); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await API.post(`/tenants/${tenantSlug}/bulk-upload?dry_run=false`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(data);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.message || 'Import failed');
      setStep('preview');
    }
  };

  const allRows = preview?.preview || [];
  const rows = previewFilter === 'all' ? allRows
    : previewFilter === 'valid' ? allRows.filter(r => r.status === 'valid')
    : allRows.filter(r => r.error_type === previewFilter);
  const totalPages = Math.ceil(rows.length / PPAGE);
  const displayRows = rows.slice((previewPage - 1) * PPAGE, previewPage * PPAGE);

  const STEP_LABELS = { idle: 'Upload', validating: 'Validating…', preview: 'Preview', importing: 'Importing…', done: 'Done' };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary)', borderRadius: '0.75rem',
        padding: '2rem', maxWidth: '780px', width: '95%',
        boxShadow: 'var(--shadow-xl)', maxHeight: '92vh', overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              Bulk Upload Users
            </h3>
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Step: <strong>{STEP_LABELS[step]}</strong> · Temporary password set to <strong>Test@123</strong>
            </p>
          </div>
          <button onClick={onClose} disabled={step === 'validating' || step === 'importing'}
            style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-tertiary)', lineHeight: 1 }}>✕</button>
        </div>

        {error && (
          <div style={{ marginBottom: '1rem', padding: '0.625rem 0.875rem', borderRadius: '0.375rem', fontSize: '0.875rem', backgroundColor: 'var(--danger-light)', color: 'var(--danger)' }}>
            {error}
          </div>
        )}

        {/* Step: idle */}
        {step === 'idle' && (
          <div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={handleDownloadTemplate}>
                ↓ Download Template
              </button>
            </div>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              style={{
                border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--border-color)'}`,
                borderRadius: '0.75rem', padding: '2.5rem 1rem', textAlign: 'center',
                backgroundColor: dragOver ? 'var(--primary-light)' : 'var(--bg-secondary)',
                transition: 'all 0.15s', cursor: 'pointer'
              }}
              onClick={() => document.getElementById('bulk-file-input').click()}
            >
              <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'center', color: 'var(--text-tertiary)' }}><Icon name="Upload" size={36} /></div>
              {file ? (
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>{file.name}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{(file.size / 1024).toFixed(1)} KB</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>Drag & drop Excel file here</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>or click to browse (.xlsx, .xls)</div>
                </div>
              )}
            </div>
            <input id="bulk-file-input" type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileInput} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleValidate} disabled={!file}>
                Validate File
              </button>
            </div>
          </div>
        )}

        {/* Step: validating / importing */}
        {(step === 'validating' || step === 'importing') && (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center', color: 'var(--primary)' }}><Icon name={step === 'validating' ? 'Search' : 'Clock'} size={36} /></div>
            <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
              {step === 'validating' ? 'Validating rows…' : 'Importing users…'}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Please wait</div>
          </div>
        )}

        {/* Step: preview */}
        {step === 'preview' && preview && (
          <div>
            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.625rem', marginBottom: '1rem' }}>
              {[
                { label: 'Total Rows', value: preview.total_rows, color: 'var(--text-primary)', bg: 'var(--bg-secondary)', filter: 'all' },
                { label: 'Ready to Import', value: preview.valid_rows, color: 'var(--success)', bg: 'var(--success-light)', filter: 'valid' },
                { label: 'Already Exist', value: preview.breakdown?.duplicates || 0, color: '#b45309', bg: '#fffbeb', filter: 'duplicate' },
                { label: 'Invalid / Missing', value: (preview.breakdown?.invalid || 0) + (preview.breakdown?.not_found || 0), color: 'var(--danger)', bg: 'var(--danger-light)', filter: 'invalid_group' },
              ].map((c, i) => (
                <div
                  key={i}
                  onClick={() => { setPreviewFilter(c.filter === 'invalid_group' ? (previewFilter === 'invalid' ? 'not_found' : 'invalid') : c.filter); setPreviewPage(1); }}
                  style={{
                    padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: c.bg, textAlign: 'center',
                    border: `1px solid ${(previewFilter === c.filter || (c.filter === 'invalid_group' && (previewFilter === 'invalid' || previewFilter === 'not_found'))) ? c.color : 'var(--border-color)'}`,
                    cursor: 'pointer', transition: 'all 0.12s'
                  }}
                >
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: c.color }}>{c.value}</div>
                  <div style={{ fontSize: '0.7rem', color: c.color, marginTop: '0.15rem', fontWeight: 500 }}>{c.label}</div>
                </div>
              ))}
            </div>

            {/* Error breakdown callout */}
            {preview.failed_rows > 0 && (
              <div style={{ marginBottom: '0.875rem', padding: '0.75rem 1rem', borderRadius: '0.5rem', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)', fontSize: '0.82rem' }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>Validation Summary</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {preview.breakdown?.duplicates > 0 && (
                    <span style={{ color: '#b45309' }}>
                      <strong>{preview.breakdown.duplicates}</strong> user{preview.breakdown.duplicates !== 1 ? 's' : ''} already exist in this tenant
                    </span>
                  )}
                  {preview.breakdown?.not_found > 0 && (
                    <span style={{ color: '#7e22ce' }}>
                      <strong>{preview.breakdown.not_found}</strong> row{preview.breakdown.not_found !== 1 ? 's' : ''} have college/department/subject not found
                    </span>
                  )}
                  {preview.breakdown?.invalid > 0 && (
                    <span style={{ color: 'var(--danger)' }}>
                      <strong>{preview.breakdown.invalid}</strong> row{preview.breakdown.invalid !== 1 ? 's' : ''} have missing or invalid fields
                    </span>
                  )}
                </div>
                {preview.valid_rows > 0 && (
                  <div style={{ marginTop: '0.4rem', color: 'var(--success)', fontWeight: 500 }}>
                    Only the {preview.valid_rows} valid row{preview.valid_rows !== 1 ? 's' : ''} will be imported — failed rows are skipped automatically.
                  </div>
                )}
              </div>
            )}

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.625rem', flexWrap: 'wrap' }}>
              {[
                { key: 'all', label: `All (${allRows.length})` },
                { key: 'valid', label: `Valid (${preview.valid_rows})`, color: 'var(--success)' },
                { key: 'duplicate', label: `Duplicates (${preview.breakdown?.duplicates || 0})`, color: '#b45309' },
                { key: 'not_found', label: `Not Found (${preview.breakdown?.not_found || 0})`, color: '#7e22ce' },
                { key: 'invalid', label: `Invalid (${preview.breakdown?.invalid || 0})`, color: 'var(--danger)' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => { setPreviewFilter(f.key); setPreviewPage(1); }}
                  style={{
                    padding: '0.25rem 0.65rem', borderRadius: '999px', border: '1px solid',
                    fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    borderColor: previewFilter === f.key ? (f.color || 'var(--primary)') : 'var(--border-color)',
                    backgroundColor: previewFilter === f.key ? (f.color || 'var(--primary)') : 'var(--bg-primary)',
                    color: previewFilter === f.key ? 'white' : 'var(--text-secondary)',
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Preview table */}
            <div style={{ overflowX: 'auto', marginBottom: '0.75rem', maxHeight: '300px', overflowY: 'auto', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: 36 }}>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>College</th>
                    <th>Dept</th>
                    <th style={{ minWidth: 90 }}>Status</th>
                    <th style={{ minWidth: 220 }}>Error Message</th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-tertiary)' }}>No rows match this filter</td></tr>
                  ) : displayRows.map((r, i) => {
                    const rowBg = r.status === 'failed'
                      ? (r.error_type === 'duplicate' ? '#fffbeb' : r.error_type === 'not_found' ? '#faf5ff' : 'var(--danger-light)')
                      : undefined;
                    const badgeStyle = r.status === 'valid'
                      ? { bg: 'var(--success-light)', color: 'var(--success)', label: 'Valid' }
                      : r.error_type === 'duplicate'
                        ? { bg: '#fef3c7', color: '#b45309', label: 'Duplicate' }
                        : r.error_type === 'not_found'
                          ? { bg: '#f3e8ff', color: '#7e22ce', label: 'Not Found' }
                          : { bg: 'var(--danger-light)', color: 'var(--danger)', label: 'Invalid' };
                    return (
                      <tr key={i} style={{ backgroundColor: rowBg }}>
                        <td style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{r.row_num || r.row_number}</td>
                        <td style={{ fontSize: '0.8rem' }}>{r.full_name || '—'}</td>
                        <td style={{ fontSize: '0.8rem' }}>{r.email || '—'}</td>
                        <td style={{ fontSize: '0.78rem' }}>{r.role || '—'}</td>
                        <td style={{ fontSize: '0.78rem' }}>{r.college_name || '—'}</td>
                        <td style={{ fontSize: '0.78rem' }}>{r.department_code || '—'}</td>
                        <td>
                          <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.45rem', borderRadius: '4px', backgroundColor: badgeStyle.bg, color: badgeStyle.color, whiteSpace: 'nowrap' }}>
                            {badgeStyle.label}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.75rem', color: r.error_type === 'duplicate' ? '#b45309' : r.error_type === 'not_found' ? '#7e22ce' : 'var(--danger)', wordBreak: 'break-word' }}>
                          {r.error_message || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                <span>Showing {(previewPage - 1) * PPAGE + 1}–{Math.min(previewPage * PPAGE, rows.length)} of {rows.length} rows</span>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button className="btn btn-secondary btn-sm" disabled={previewPage === 1} onClick={() => setPreviewPage(p => p - 1)}>Prev</button>
                  <button className="btn btn-secondary btn-sm" disabled={previewPage === totalPages} onClick={() => setPreviewPage(p => p + 1)}>Next</button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
              {preview.valid_rows === 0 && (
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  No rows to import — fix errors or upload a different file.
                </span>
              )}
              <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setStep('idle'); setPreview(null); setPreviewFilter('all'); }}>
                  Back
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleImport}
                  disabled={preview.valid_rows === 0}
                >
                  Import {preview.valid_rows} Valid Row{preview.valid_rows !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step: done */}
        {step === 'done' && result && (
          <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}><Icon name="CheckCircle" size={44} color="#16a34a" /></div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
              Import Complete
            </div>
            <div style={{ display: 'inline-grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem', textAlign: 'left' }}>
              <div style={{ padding: '0.875rem 1.25rem', borderRadius: '0.5rem', backgroundColor: 'var(--success-light)', border: '1px solid var(--success)' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--success)' }}>{result.imported_rows}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--success)' }}>Imported</div>
              </div>
              <div style={{ padding: '0.875rem 1.25rem', borderRadius: '0.5rem', backgroundColor: result.failed_rows > 0 ? 'var(--danger-light)' : 'var(--bg-secondary)', border: `1px solid ${result.failed_rows > 0 ? 'var(--danger)' : 'var(--border-color)'}` }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: result.failed_rows > 0 ? 'var(--danger)' : 'var(--text-secondary)' }}>{result.failed_rows}</div>
                <div style={{ fontSize: '0.8rem', color: result.failed_rows > 0 ? 'var(--danger)' : 'var(--text-secondary)' }}>Failed</div>
              </div>
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
              Batch ID: <strong>#{result.batch_id}</strong>
            </div>
            <button className="btn btn-primary btn-sm" onClick={onSuccess}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
